import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { marketplaceApi, addonsApi, type MarketplaceApp } from '../api/client'
import { useInstances } from '../hooks/useInstances'
import { useAuthStore } from '../store/authStore'
import { Badge, Button, Card, Field, Select, Skeleton } from '../components/ui'

function formatPrice(app: MarketplaceApp, t: (k: string, opts?: Record<string, unknown>) => string) {
  if (app.price_cents === 0) return t('marketplace.priceFree')
  return t('marketplace.priceOneTime', {
    price: (app.price_cents / 100).toFixed(0),
    currency: app.currency,
  })
}

export default function MarketplaceAppPage() {
  const { t } = useTranslation()
  const { slug = '' } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const token = useAuthStore((s) => s.accessToken)

  const { data: app, isLoading, isError } = useQuery({
    queryKey: ['marketplace-app', slug],
    queryFn: () => marketplaceApi.get(slug),
    enabled: !!slug,
    staleTime: 60_000,
  })

  const { data: instances } = useInstances()
  const runningInstances = (instances ?? []).filter(
    (i) => (i as { status?: string }).status === 'running',
  )
  const [selectedInstance, setSelectedInstance] = useState('')

  const installMutation = useMutation({
    mutationFn: ({ instanceId, githubUrl, ref }: { instanceId: string; githubUrl: string; ref: string }) =>
      addonsApi.installFromGitHub(instanceId, githubUrl, ref, 'public'),
    onSuccess: (_data, variables) => {
      navigate(`/instances/${variables.instanceId}/addons`)
    },
  })

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-3xl">
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }
  if (isError || !app) {
    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-3xl">
        <p className="text-sm text-gray-500">{t('marketplace.notFound')}</p>
        <Link to="/apps" className="text-sm text-brand-600 hover:text-brand-700">
          {t('marketplace.backToApps')}
        </Link>
      </div>
    )
  }

  const handleDownload = () => {
    if (!token) return
    window.location.href = marketplaceApi.downloadUrl(app.slug, token)
  }

  const handleInstall = () => {
    if (!selectedInstance) return
    installMutation.mutate({
      instanceId: selectedInstance,
      githubUrl: app.source_ref,
      ref: app.github_ref || 'main',
    })
  }

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-3xl">
      <Link
        to="/apps"
        className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-4"
      >
        ← {t('marketplace.backToApps')}
      </Link>

      <Card className="p-6 mb-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="text-5xl shrink-0">{app.icon || '📦'}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{app.name}</h1>
              <span className="text-xs text-gray-400">v{app.version}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{app.tagline}</p>
            <div className="flex items-center gap-3 mt-3">
              <span
                className={`text-sm font-semibold ${
                  app.price_cents === 0 ? 'text-emerald-600' : 'text-gray-900'
                }`}
              >
                {formatPrice(app, t)}
              </span>
              {app.kind === 'downloadable' ? (
                <Badge tone="warning">{t('marketplace.kindDownloadable')}</Badge>
              ) : (
                <Badge tone="accent">{t('marketplace.kindInstallable')}</Badge>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-700 whitespace-pre-line">{app.description}</p>
      </Card>

      {app.kind === 'downloadable' ? (
        <div className="space-y-4">
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
            <p className="text-sm font-semibold text-amber-900 mb-1">
              {t('marketplace.sourceOdooHintTitle')}
            </p>
            <p className="text-sm text-amber-800">{t('marketplace.sourceOdooHint')}</p>
          </div>
          <Button onClick={handleDownload} disabled={!token} className="w-full sm:w-auto">
            {t('marketplace.download')}
          </Button>
        </div>
      ) : (
        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {t('marketplace.installToInstance')}
          </h2>

          {runningInstances.length === 0 ? (
            <div className="text-sm text-gray-500">
              {t('marketplace.noRunningInstance')}{' '}
              <Link to="/instances/new" className="text-brand-600 hover:text-brand-700 font-medium">
                {t('marketplace.createInstance')}
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label={t('marketplace.selectInstance')}>
                {(p) => (
                  <Select
                    {...p}
                    value={selectedInstance}
                    onChange={(e) => setSelectedInstance(e.target.value)}
                  >
                    <option value="">{t('marketplace.selectInstancePlaceholder')}</option>
                    {runningInstances.map((inst) => (
                      <option key={(inst as { id: string }).id} value={(inst as { id: string }).id}>
                        {(inst as { name?: string; subdomain?: string }).name ?? (inst as { subdomain?: string }).subdomain}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {installMutation.isError && (
                <p className="text-sm text-red-600">
                  {(installMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                    t('marketplace.installError')}
                </p>
              )}

              <Button
                onClick={handleInstall}
                disabled={!selectedInstance}
                loading={installMutation.isPending}
                className="w-full sm:w-auto"
              >
                {installMutation.isPending ? t('marketplace.installing') : t('marketplace.installToInstance')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
